<template>
  <div
    class="slider-verify"
    :class="{ 'is-success': verified, 'is-dragging': dragging }"
    :style="{ width: width }"
    ref="trackRef"
  >
    <div class="slider-verify__progress" :style="{ width: progressWidth + 'px' }"></div>
    <div class="slider-verify__text">
      {{ verified ? '验证通过' : '拖动滑块验证' }}
    </div>
    <div
      class="slider-verify__handle"
      :style="{ transform: `translateX(${offsetX}px) translateY(-50%)` }"
      @mousedown.prevent="handleDragStart"
      @touchstart.prevent="handleDragStart"
    >
      <img
        :src="verified ? successIcon : sliderIcon"
        alt=""
        class="slider-verify__icon"
      />
    </div>
  </div>
</template>

<script>
import { encrypt } from '@/utils/jsencrypt'
import { getPhone } from '@/api/login'

export default {
  name: 'SliderVerify',
  props: {
    account: {
      type: String,
      default: ''
    },
    password: {
      type: String,
      default: ''
    },
    width: {
      type: String,
      default: '464px'
    }
  },
  data() {
    return {
      sliderIcon: require('@/assets/images/login/slider-icon.png'),
      successIcon: require('@/assets/images/login/seccess-icon.png'),
      offsetX: 0,
      maxOffset: 0,
      handleWidth: 0,
      dragging: false,
      verifying: false,
      verified: false,
      startX: 0,
      startOffset: 0
    }
  },
  computed: {
    progressWidth() {
      return this.offsetX + this.handleWidth
    }
  },
  watch: {
    account() {
      this.reset()
    },
    password() {
      this.reset()
    }
  },
  mounted() {
    this.calcBounds()
    window.addEventListener('resize', this.calcBounds)
  },
  beforeDestroy() {
    this.removeDragListeners()
    window.removeEventListener('resize', this.calcBounds)
  },
  methods: {
    calcBounds() {
      this.$nextTick(() => {
        const track = this.$refs.trackRef
        const handle = track && track.querySelector('.slider-verify__handle')
        if (!track || !handle) return
        this.handleWidth = handle.offsetWidth
        this.maxOffset = Math.max(track.offsetWidth - this.handleWidth - 32, 0)
        if (!this.verified && this.offsetX > this.maxOffset) {
          this.offsetX = this.maxOffset
        }
      })
    },
    handleDragStart(e) {
      if (this.verified || this.verifying) return
      if (!this.account || !this.password) {
        this.$message.warning('请先输入账号和密码')
        return
      }
      this.dragging = true
      this.startX = this.getClientX(e)
      this.startOffset = this.offsetX
      document.addEventListener('mousemove', this.handleDragMove)
      document.addEventListener('mouseup', this.handleDragEnd)
      document.addEventListener('touchmove', this.handleDragMove, { passive: false })
      document.addEventListener('touchend', this.handleDragEnd)
    },
    handleDragMove(e) {
      if (!this.dragging || this.verified) return
      e.preventDefault()
      const moveX = this.getClientX(e) - this.startX
      let nextOffset = this.startOffset + moveX
      if (nextOffset < 0) nextOffset = 0
      if (nextOffset > this.maxOffset) nextOffset = this.maxOffset
      this.offsetX = nextOffset
    },
    async handleDragEnd() {
      if (!this.dragging) return
      this.dragging = false
      this.removeDragListeners()
      if (this.offsetX >= this.maxOffset - 2) {
        await this.verifySlider()
      } else {
        this.offsetX = 0
      }
    },
    async verifySlider() {
      this.verifying = true
      this.offsetX = this.maxOffset
      try {
        const res = await getPhone({
          username: this.account.trim(),
          password: encrypt(this.password),
          deviceType:'pc_web'
        })
        if (res && res.code === 200) {
          this.verified = true
          this.$emit('success', res.data)
        } else {
          this.reset()
          this.$message.error(res?.msg || '验证失败，请重试')
          this.$emit('error', res)
        }
      } catch (error) {
        this.reset()
        this.$emit('error', error)
      } finally {
        this.verifying = false
      }
    },
    reset() {
      this.offsetX = 0
      this.verified = false
      this.verifying = false
      this.dragging = false
      this.removeDragListeners()
    },
    removeDragListeners() {
      document.removeEventListener('mousemove', this.handleDragMove)
      document.removeEventListener('mouseup', this.handleDragEnd)
      document.removeEventListener('touchmove', this.handleDragMove)
      document.removeEventListener('touchend', this.handleDragEnd)
    },
    getClientX(e) {
      if (e.touches && e.touches.length) {
        return e.touches[0].clientX
      }
      if (e.changedTouches && e.changedTouches.length) {
        return e.changedTouches[0].clientX
      }
      return e.clientX
    }
  }
}
</script>

<style lang="scss" scoped>
.slider-verify {
  position: relative;
  width: 464px;
  height: 52px;
  display: flex;
  place-content: center;
  place-items: center;
  gap: 16px;
  flex-shrink: 0;
  padding: 12px 16px;
  box-sizing: border-box;
  background: rgba(243, 245, 249, 1);
  overflow: hidden;
  user-select: none;

  &.is-success {
    background: rgba(32, 201, 128, 1);

    .slider-verify__text {
      color: #fff;
    }

    .slider-verify__progress {
      display: none;
    }

    .slider-verify__handle {
      border-color: rgba(32, 201, 128, 1);
      cursor: default;
    }
  }
}

.slider-verify__progress {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: rgba(32, 201, 128, 0.15);
  pointer-events: none;
  transition: width 0.05s linear;
}

.slider-verify__text {
  position: relative;
  z-index: 1;
  color: #909399;
  font-size: 14px;
  line-height: 1;
  pointer-events: none;
}

.slider-verify__handle {
  position: absolute;
  left: 16px;
  top: 50%;
  z-index: 2;
  width: fit-content;
  height: fit-content;
  display: inline-flex;
  place-content: flex-start;
  place-items: center;
  gap: 10px;
  box-sizing: border-box;
  padding: 9px 18px;
  border: 1px solid rgba(206, 206, 206, 1);
  background: rgba(255, 255, 255, 1);
  transform: translateY(-50%);
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
}

.slider-verify__icon {
  width: 28px;
  height: 28px;
  display: block;
}
</style>
